/**
 * Decoder for HERE's Flexible Polyline encoding.
 *
 * HERE Routing v8 returns each route section's geometry as a Flexible Polyline
 * string (a compact, variable-precision delta encoding). We only ever DECODE
 * (the API produces it) and only need the 2D lat/lng track, so this is a trimmed
 * port of HERE's reference decoder.
 *
 * Verified against HERE's published test vector AND a live Routing v8 response
 * (decodes to the exact origin/destination coordinates).
 *
 * Ref: https://github.com/heremaps/flexible-polyline
 */

// URL-safe base-64 alphabet used by Flexible Polyline. Index = encoded value.
const ENCODING_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

// char -> 6-bit value lookup, built from the alphabet (no hand-transcription).
const CHAR_TO_VALUE: Record<string, number> = (() => {
  const map: Record<string, number> = {};
  for (let i = 0; i < ENCODING_ALPHABET.length; i += 1) {
    map[ENCODING_ALPHABET[i]] = i;
  }
  return map;
})();

/**
 * Decode the stream into unsigned integers. Uses `* 2 ** shift` (not `<< shift`)
 * so large multi-group values never hit the 32-bit bitwise overflow.
 */
function decodeUnsignedValues(encoded: string): number[] {
  let result = 0;
  let shift = 0;
  const values: number[] = [];

  for (const char of encoded) {
    const value = CHAR_TO_VALUE[char];
    if (value === undefined) continue;
    result += (value & 0x1f) * 2 ** shift;
    if ((value & 0x20) === 0) {
      values.push(result);
      result = 0;
      shift = 0;
    } else {
      shift += 5;
    }
  }
  return values;
}

/** Reverse the zig-zag signed-integer encoding. */
function toSigned(value: number): number {
  if (value % 2 === 1) return -(Math.floor(value / 2) + 1);
  return Math.floor(value / 2);
}

/**
 * Decode a Flexible Polyline string into `[lat, lng]` pairs.
 *
 * Returns an empty array for empty/invalid input so callers can fall back
 * safely (a bad geometry should never throw during render).
 */
export function decodeFlexiblePolyline(encoded: string): Array<[number, number]> {
  if (!encoded) return [];
  try {
    const decoder = decodeUnsignedValues(encoded);
    if (decoder.length < 4) return [];

    // Header: decoder[0] = version, decoder[1] = packed content.
    const headerContent = decoder[1];
    const precision = headerContent & 15;
    const thirdDim = (headerContent >> 4) & 7;
    const factorDegree = 10 ** precision;

    let lastLat = 0;
    let lastLng = 0;
    const positions: Array<[number, number]> = [];
    // Step over the (optional) third dimension so we only keep lat/lng.
    const stride = thirdDim ? 3 : 2;

    for (let i = 2; i + 1 < decoder.length; i += stride) {
      lastLat += toSigned(decoder[i]);
      lastLng += toSigned(decoder[i + 1]);
      positions.push([lastLat / factorDegree, lastLng / factorDegree]);
    }
    return positions;
  } catch {
    return [];
  }
}
